import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { FEATURES } from '@genealogy-app/shared';
import { AppLayout } from '@/components/Layout/AppLayout';
import { useTreeFeatures } from '@/hooks/useTreeFeatures';

interface Props {
  /** Schlüssel aus FEATURES, z. B. 'lifelines'. */
  feature: string;
}

/**
 * Erlaubt Zugriff nur, wenn die Funktion im Tarif des Stammbaums enthalten ist.
 *
 * Abgrenzung zu RequirePermission: dort geht es darum, was jemand DARF – hier
 * darum, ob es die Sache in diesem Stammbaum überhaupt GIBT. Beides muss
 * stimmen, und beides wird getrennt geprüft.
 *
 * Statt auf /no-access umzuleiten steht hier eine eigene Seite: „kein Recht"
 * und „nicht gebucht" sind verschiedene Dinge, und wer die Meldung liest, soll
 * wissen, an wen er sich wendet – bei Rechten an den Besitzer, beim Tarif an
 * den Betreiber.
 *
 * Maßgeblich ist der Tarif des BESITZERS. In einem fremden Stammbaum hilft der
 * eigene Tarif nicht weiter.
 */
export function RequireFeature({ feature }: Props) {
  const navigate = useNavigate();
  const { hat, tarif, geladen, nachpruefen } = useTreeFeatures();
  const [nachgefragt, setNachgefragt] = useState(false);

  const gesperrt = geladen && !hat(feature);

  // Bevor die Sperrseite erscheint, EINMAL beim Server nachfragen.
  //
  // Die Liste im Speicher kann veraltet sein: der Betreiber schaltet eine
  // Funktion frei, die laufende Sitzung weiß noch nichts davon und behauptet,
  // sie sei nicht gebucht. Genau so ist der Fehler aufgefallen – abmelden und
  // wieder anmelden „half", weil dabei alles neu geladen wurde.
  //
  // Der Normalfall (erlaubt) kostet nichts. Nur der Sperrfall fragt nach, und
  // der ist selten und folgenreich genug, um eine Anfrage wert zu sein.
  useEffect(() => {
    if (!gesperrt || nachgefragt) return;
    let abgebrochen = false;
    void nachpruefen().finally(() => {
      if (!abgebrochen) setNachgefragt(true);
    });
    return () => {
      abgebrochen = true;
    };
  }, [gesperrt, nachgefragt, nachpruefen]);

  // Beim Wechsel auf eine andere Funktion wieder nachfragen dürfen.
  useEffect(() => {
    setNachgefragt(false);
  }, [feature]);

  // Solange nichts geladen ist, gilt alles als erlaubt – sonst blitzte die
  // Sperrseite bei jedem Direktaufruf kurz auf.
  if (!geladen || hat(feature)) return <Outlet />;

  // Gesperrt, aber die Nachfrage läuft noch: lieber kurz warten als falsch
  // sperren.
  if (!nachgefragt) {
    return (
      <AppLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        </div>
      </AppLayout>
    );
  }

  const bezeichnung = FEATURES.find((f) => f.key === feature)?.label ?? feature;

  return (
    <AppLayout>
      <div className="mx-auto max-w-lg px-4 py-16 sm:px-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <Lock className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" />
          <h1 className="mt-3 text-lg font-semibold text-slate-900">
            {bezeichnung} ist nicht freigeschaltet
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {tarif ? (
              <>
                Dieser Stammbaum läuft im Tarif{' '}
                <span className="font-medium text-slate-900">{tarif}</span>, und darin
                ist {bezeichnung} nicht enthalten.
              </>
            ) : (
              <>Diese Funktion ist für diesen Stammbaum nicht freigeschaltet.</>
            )}{' '}
            Der Besitzer des Stammbaums kann den Tarif beim Betreiber erweitern
            lassen.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Zurück zum Stammbaum
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

export default RequireFeature;
